import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  CoolifyServerDto,
  CreateDatabaseInput,
  DatabaseCredentialsDto,
  DatabaseEngine,
  PlanFeatures,
  PlanLimits,
  ResourceDto,
  SubscriptionDto,
} from '@cpm/shared';
import { DATABASE_ENGINE_INFO, DATABASE_ENGINES } from '@cpm/shared';
import { ArrowLeft, Check, Copy, Database, KeyRound, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  cx,
} from '../components/ui.js';

interface SubscriptionDetailResponse {
  subscription: SubscriptionDto;
  limits: PlanLimits;
  features: PlanFeatures;
}

interface CreateDatabaseResponse {
  database: ResourceDto;
  credentials: DatabaseCredentialsDto;
  warning: string | null;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
      aria-label="Copy to clipboard"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="size-4 text-emerald-500" /> : <Copy className="size-4" />}
    </button>
  );
}

function CredentialRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
      <dt className="shrink-0 text-sm text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="flex min-w-0 items-center gap-1">
        <code className="truncate rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">
          {value}
        </code>
        <CopyButton value={value} />
      </dd>
    </div>
  );
}

/**
 * Shown exactly once, straight from the create response — the panel never
 * stores these values and no API will return them again (ADR-0007).
 */
export function CredentialsReveal({
  result,
  onContinue,
  customer = false,
}: {
  customer?: boolean;
  result: Pick<CreateDatabaseResponse, 'credentials' | 'warning'>;
  onContinue: () => void;
}) {
  const { credentials } = result;
  return (
    <Card className="mx-auto max-w-xl">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <KeyRound className="size-5 text-amber-500" /> Database credentials
          </span>
        }
        description="Copy these now — they are shown only once and cannot be recovered from the panel."
      />
      <div className="space-y-4 px-5 py-4">
        {result.warning && <ErrorNote error={result.warning} />}
        <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300">
          <TriangleAlert className="mr-1.5 inline size-4 align-text-bottom" />
          The panel does not store these credentials. {customer ? 'If they are lost, contact your provider to reset them.' : 'If they are lost, reset them in Coolify.'}
        </div>
        <dl>
          {credentials.username && <CredentialRow label="Username" value={credentials.username} />}
          <CredentialRow label="Password" value={credentials.password} />
          {credentials.rootPassword && (
            <CredentialRow label="Root password" value={credentials.rootPassword} />
          )}
          {credentials.database && <CredentialRow label="Database" value={credentials.database} />}
          {credentials.internalUrl && (
            <CredentialRow label="Internal URL" value={credentials.internalUrl} />
          )}
        </dl>
        <div className="flex justify-end border-t border-slate-100 pt-4 dark:border-slate-800">
          <Button onClick={onContinue}>I saved the credentials — continue</Button>
        </div>
      </div>
    </Card>
  );
}

export function DatabaseCreatePage() {
  const { id: subscriptionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [engine, setEngine] = useState<DatabaseEngine>('postgresql');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [serverUuid, setServerUuid] = useState('');
  const [image, setImage] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [publicPort, setPublicPort] = useState('');
  const [instantDeploy, setInstantDeploy] = useState(true);
  const [created, setCreated] = useState<CreateDatabaseResponse | null>(null);

  const detail = useQuery({
    queryKey: ['subscriptions', subscriptionId],
    queryFn: () => api.get<SubscriptionDetailResponse>(`/api/subscriptions/${subscriptionId}`),
    enabled: Boolean(subscriptionId),
  });
  const servers = useQuery({
    queryKey: ['coolify', 'servers'],
    queryFn: () => api.get<CoolifyServerDto[]>('/api/coolify/servers'),
  });

  const create = useMutation({
    mutationFn: (input: CreateDatabaseInput) =>
      api.post<CreateDatabaseResponse>('/api/databases/', input),
    onSuccess: setCreated,
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const { subscription, features, limits } = detail.data;

  if (created) {
    return (
      <CredentialsReveal
        result={created}
        onContinue={() => void navigate(`/databases/${created.database.id}`)}
      />
    );
  }

  const info = DATABASE_ENGINE_INFO[engine];
  const canSubmit =
    name.trim() !== '' && serverUuid !== '' && (!isPublic || publicPort.trim() !== '');

  return (
    <div>
      <Link
        to={`/subscriptions/${subscriptionId}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> {subscription.name}
      </Link>
      <PageHeader
        title="New database"
        description={
          <>
            Provisioned into the Coolify project of <strong>{subscription.name}</strong> — plan
            limits apply. Credentials are generated and shown once after creation.
          </>
        }
      />

      {!subscription.coolifyProjectUuid && (
        <div className="mb-4">
          <ErrorNote error="This subscription has no linked Coolify project — databases cannot be provisioned." />
        </div>
      )}

      <Card>
        <CardHeader title="Engine & placement" />
        <div className="space-y-5 px-5 py-5">
          <div>
            <Label>Engine</Label>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {DATABASE_ENGINES.map((key) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setEngine(key)}
                  className={cx(
                    'flex items-center gap-2.5 rounded-lg border p-3 text-left text-sm transition-colors',
                    engine === key
                      ? 'border-brand-600 bg-brand-50 dark:border-brand-400 dark:bg-brand-500/10'
                      : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600',
                  )}
                >
                  <Database className="size-4 shrink-0 text-slate-400" />
                  <span>
                    <span className="block font-medium">{DATABASE_ENGINE_INFO[key].label}</span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      :{DATABASE_ENGINE_INFO[key].internalPort}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="db-name">Name</Label>
              <Input id="db-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="db-server">Server</Label>
              {servers.isPending ? (
                <Spinner label="Loading servers…" />
              ) : servers.error ? (
                <ErrorNote error={servers.error} />
              ) : (
                <Select
                  id="db-server"
                  value={serverUuid}
                  onChange={(e) => setServerUuid(e.target.value)}
                >
                  <option value="" disabled>
                    Select a server…
                  </option>
                  {servers.data.map((server) => (
                    <option key={server.uuid} value={server.uuid} disabled={!server.usable}>
                      {server.name} ({server.ip}){server.usable ? '' : ' — not usable'}
                    </option>
                  ))}
                </Select>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="db-image">Image (version pinning, optional)</Label>
              <Input
                id="db-image"
                placeholder={info.imagePlaceholder}
                value={image}
                onChange={(e) => setImage(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Leave empty for Coolify&apos;s default; pin a tag to control upgrades.
              </p>
            </div>
            <div>
              <Label htmlFor="db-desc">Description (optional)</Label>
              <Input
                id="db-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                disabled={!features.database_external_access}
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              Expose on a public port
              {!features.database_external_access && (
                <span className="text-xs font-normal text-amber-600 dark:text-amber-400">
                  (plan does not include external database access)
                </span>
              )}
            </label>
            {isPublic && (
              <div>
                <Label htmlFor="db-port">Public port (1024–65535)</Label>
                <Input
                  id="db-port"
                  type="number"
                  min={1024}
                  max={65535}
                  value={publicPort}
                  onChange={(e) => setPublicPort(e.target.value)}
                  placeholder={`e.g. 5${info.internalPort}`}
                />
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                checked={instantDeploy}
                onChange={(e) => setInstantDeploy(e.target.checked)}
              />
              Start immediately after creation
            </label>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Container limits from plan:{' '}
              {limits.memory_mb == null ? 'unlimited memory' : `${limits.memory_mb} MB`},{' '}
              {limits.cpu_cores == null ? 'unlimited CPU' : `${limits.cpu_cores} CPU`}
            </span>
          </div>

          {create.error != null && <ErrorNote error={create.error} />}

          <div className="flex justify-end border-t border-slate-100 pt-4 dark:border-slate-800">
            <Button
              loading={create.isPending}
              disabled={!canSubmit || !subscription.coolifyProjectUuid}
              onClick={() =>
                create.mutate({
                  subscriptionId: subscriptionId!,
                  engine,
                  serverUuid,
                  name,
                  description: description || null,
                  image: image || undefined,
                  isPublic,
                  publicPort: isPublic ? Number(publicPort) : undefined,
                  instantDeploy,
                })
              }
            >
              <Database className="size-4" /> Create database
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
